import { useState } from 'react';
import { T, Var } from 'gt-react';
import { ArrowClockwiseIcon, PencilIcon } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { useWorkflowDraftManager } from '@/api/rpc/hooks/workflows/useWorkflowDraftManager';

export function StaleDraftDialog({
  manager,
}: {
  manager: ReturnType<typeof useWorkflowDraftManager>;
}) {
  const [error, setError] = useState<string | null>(null);
  const entry = manager.pendingEntry;
  const freshness = entry?.result.freshness;
  const choose = async (
    decision: Parameters<typeof manager.chooseDraft>[0],
  ) => {
    setError(null);
    try {
      await manager.chooseDraft(decision);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };
  return (
    <Dialog
      open={entry !== null}
      onOpenChange={(open) => {
        if (!open && !manager.isCreatingDraft) void choose('cancel');
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            <T context="workflow draft resume decision title">
              Review Your Existing Draft
            </T>
          </DialogTitle>
          <DialogDescription>
            <T context="existing workflow draft age and original base version">
              Your draft v<Var>{entry?.result.definition.version}</Var> was
              forked from{' '}
              <Var>
                {freshness?.parentVersionNumber == null
                  ? '—'
                  : `v${freshness.parentVersionNumber}`}
              </Var>{' '}
              and last edited{' '}
              <Var>{freshness ? Math.floor(freshness.idleDays) : '—'}</Var> days
              ago.
            </T>{' '}
            {freshness?.pinnedVersionNumber != null && (
              <T context="current live workflow version in stale draft dialog">
                v<Var>{freshness.pinnedVersionNumber}</Var> is live.
              </T>
            )}
          </DialogDescription>
        </DialogHeader>
        {entry?.result.requestedSourceIgnored && (
          <p className="text-sm text-muted-foreground">
            <T context="server resumed an existing draft instead of forking the selected workflow version">
              Your existing draft was resumed instead of starting from the
              version you picked.
            </T>
          </p>
        )}
        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}
        <DialogFooter className="flex-col sm:flex-col items-stretch gap-2">
          {freshness?.isBehindPinned && (
            <Button
              disabled={manager.isCreatingDraft}
              onClick={() => void choose('live')}
            >
              <ArrowClockwiseIcon className="h-4 w-4 mr-1" />
              <T context="replace stale draft with a fresh draft from the live workflow">
                Discard Draft and Start from v
                <Var>{freshness.pinnedVersionNumber}</Var>
              </T>
            </Button>
          )}
          {entry?.result.requestedSourceIgnored &&
            entry.params.sourceVersionId &&
            entry.params.sourceVersionId !== freshness?.pinnedVersionId && (
              <Button
                variant="outline"
                disabled={manager.isCreatingDraft}
                onClick={() => void choose('requested')}
              >
                <ArrowClockwiseIcon className="h-4 w-4 mr-1" />
                <T context="replace existing draft with a draft from the originally selected version">
                  Discard Draft and Start from{' '}
                  <Var>
                    {entry.params.sourceVersionNumber == null
                      ? 'Selected Version'
                      : `v${entry.params.sourceVersionNumber}`}
                  </Var>
                </T>
              </Button>
            )}
          <Button
            variant={freshness?.isBehindPinned ? 'outline' : 'default'}
            disabled={manager.isCreatingDraft}
            onClick={() => void choose('continue')}
          >
            <PencilIcon className="h-4 w-4 mr-1" />
            <T context="resume the existing workflow draft despite its age or base">
              Continue Editing v<Var>{entry?.result.definition.version}</Var>
            </T>
          </Button>
          <Button
            variant="ghost"
            disabled={manager.isCreatingDraft}
            onClick={() => void choose('cancel')}
          >
            <T context="cancel entering a workflow draft">Cancel</T>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
